import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '../context/ThemeContext';

const tokenomicsData = [
  { name: 'Team', value: 15, color: '#0055FF' },
  { name: 'Development', value: 20, color: '#00D4FF' },
  { name: 'Liquidity', value: 25, color: '#FFB800' },
  { name: 'Community', value: 25, color: '#8B5CF6' },
  { name: 'Marketing', value: 15, color: '#10B981' },
];

const utilities = [
  'Transaction fees & gas payments',
  'Governance voting rights',
  'Staking rewards',
  'DApp deployment fees',
  'Cross-chain bridge fees',
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="px-4 py-2 rounded-lg bg-black/80 backdrop-blur-md border border-white/10">
        <p className="text-white font-semibold">{`${payload[0].name}: ${payload[0].value}%`}</p>
      </div>
    );
  }
  return null;
};

export const Tokenomics = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="tokenomics"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#050505]' : 'bg-[#F8FAFC]'}`}
      ref={ref}
      data-testid="tokenomics-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            Tokenomics
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <span className="gradient-text">Polk Coin</span> Distribution
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            A fair and sustainable token distribution designed for long-term ecosystem growth.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Chart */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tokenomicsData}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={150}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {tokenomicsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              {/* Center text */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>1B</div>
                <div className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>Total Supply</div>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              {tokenomicsData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {item.name} ({item.value}%)
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Art & Utility */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Token art */}
            <div className="relative rounded-3xl overflow-hidden mb-8">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/e8f5391f-27da-4e56-b4ea-a3f7a756238b/images/875fe34c9842118654025be96f77ddb164655cf512579017231f373733a172ed.png"
                alt="Tokenomics Visualization"
                className="w-full h-auto rounded-3xl"
              />
            </div>

            {/* Token Utility */}
            <div className={`p-8 rounded-3xl border ${
              isDark 
                ? 'bg-white/[0.03] border-white/10' 
                : 'bg-white border-black/5'
            }`}>
              <h3 className={`text-xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Token Utility
              </h3>
              <ul className="space-y-4">
                {utilities.map((utility, index) => (
                  <motion.li
                    key={utility}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className={isDark ? 'text-zinc-300' : 'text-slate-700'}>{utility}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
